import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Grid, MenuItem, styled, Stack, Button, CircularProgress } from '@mui/material';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  saveClientLegal,
  ClientLegelDTO,
  ILegalIdentifierSettings,
  createNewLegalIdentifier,
  saveActivityLog,
  LogEntityType,
  LogAction,
  LogContextType,
} from 'wisestep-api';
import { ControlledTextInput, WsButton, Label, WsColors } from 'wisestep-base';
import { object, string } from 'yup';

import DialogWithInput from '../../../../components/dialogWithInput';
import { SelectWCreate } from '../../../../components/SelectWCreate';
import { activityLogDTOHelper } from '../../../../features/activityLog/ActivityHelper';
import { failed, success, warning } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import { legalIdentifierDefaultPayload } from '../../../clientsSettings/helper/ClientSettingsHelper';
import IClientLegel from '../../types/IClientLegel';

interface ClientLegalIdentifiersProps {
  index: number;
  clientLegal: IClientLegel;
  legalIdentifierTypes: ILegalIdentifierSettings[];
  getLegalIdentifierList: () => Promise<void>;
  showModal?: () => void;
  editFlag?: boolean;
  loadTabData?: (pageNo: number) => void;
}

const validationSchema = object().shape({
  legalIdentifierId: string().required('Please select Legal Identifier Type'),
  value: string().required('Please provide Identifier Value'),
});

const DrawerFooter = styled(Grid)(() => ({
  position: 'fixed',
  bottom: 1,
  justifyContent: 'flex-start',
  background: WsColors.TertiaryShades100,
  width: '50vw',
  marginLeft: '-1.5vw',
}));

const ClientLegalIdentifierForm = ({
  index,
  clientLegal,
  legalIdentifierTypes = [],
  getLegalIdentifierList,
  loadTabData,
  showModal,
  editFlag = false,
}: ClientLegalIdentifiersProps) => {
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);

  const methods = useForm<any>({
    resolver: yupResolver(validationSchema),
    defaultValues: clientLegal.data,
  });

  const {
    handleSubmit,
    setValue,
    watch,
    formState: { dirtyFields },
  } = methods;

  const value = watch(`value`);
  const legalIdentifierId = watch(`legalIdentifierId`);
  const setNotification = useNotificationStore((state) => state.setNotification);

  useEffect(() => {
    setValue(`legalIdentifierId`, clientLegal?.data.legalIdentifierDTO.id);
    setValue(`value`, clientLegal?.data.value);
  }, [clientLegal, setValue]);

  useEffect(() => {
    clientLegal.data.legalIdentifierDTO.id = legalIdentifierId;
    clientLegal.data.value = value;
  }, [clientLegal, value, legalIdentifierId]);

  const saveData = async (data: ClientLegelDTO) => {
    try {
      const response = await saveClientLegal(data);
      setNotification(success('Legal identifiers has been saved successfully !!'));
      return response.data.data;
    } catch {
      setNotification(failed('Something went wrong, Please try again !!'));
    }
  };

  const onSubmit = async (data) => {
    setIsLoading(true);
    clientLegal.data.legalIdentifierDTO.id = data.legalIdentifierId;
    clientLegal.data.value = data.value;
    clientLegal.isEditable = false;
    clientLegal.data = await saveData(clientLegal.data);
    const avPayload = { ...activityLogDTOHelper };
    const avChangedValueViaDirtyFileds = Object.keys(dirtyFields).toString().split(',');
    if (data.id) {
      avPayload.action = LogAction.Edit;
      avPayload.description = `EDITED ${Object.keys(
        dirtyFields
      ).toString()} for Legal Identifier under`;
    } else {
      avPayload.action = LogAction.Add;
      avPayload.description = `ADDED ${clientLegal.data.legalIdentifierDTO.name} for Legal Identifier under`;
    }
    avPayload.entityType = LogEntityType.Legal;
    avPayload.parentEntitiyType = LogContextType.Client;
    avPayload.parentEntitiyId = data.clientId;
    avPayload.entityId = clientLegal.data.legalIdentifierDTO.id;
    avPayload.field = `Legal Identifier`;
    avPayload.entityName = clientLegal.data.legalIdentifierDTO.name;
    avPayload.contextData = { clientId: data.clientId };
    avChangedValueViaDirtyFileds[0].length > 0 ? await saveActivityLog(avPayload) : '';
    showModal && showModal();
    loadTabData && loadTabData(0);
    setIsLoading(false);
  };

  const onAddLegalIdentifier = async (data) => {
    const payload = { ...legalIdentifierDefaultPayload };
    payload.name = data.fieldOne;
    payload.country = data.country;
    try {
      const response = await createNewLegalIdentifier(payload);
      setNotification(success(`${data.fieldOne} has been added successfully !!`));
      await getLegalIdentifierList();
      setOpenDialog(false);
      methods.setValue('legalIdentifierId', response.data.id);
    } catch (error: any) {
      if (error.response.status === 400)
        setNotification(warning(`Oops , ${data.fieldOne} already exists!!`));
      else setNotification(failed('Something went wrong !!'));
    }
  };

  return (
    <>
      <FormProvider {...methods} key={index}>
        <Box component="form" onSubmit={handleSubmit((data) => onSubmit(data))}>
          <Grid container spacing={2} sx={{ px: 3, pt: 1, pb: 12 }}>
            <Grid item sm={5}>
              <Label>Legal Identifier Type *</Label>
              <SelectWCreate
                placeholder=""
                onCreate={() => {
                  setOpenDialog(true);
                }}
                name="legalIdentifierId"
                disabled={!clientLegal.isEditable}
                defaultValue={clientLegal.data?.legalIdentifierDTO?.name ?? ''}
              >
                {legalIdentifierTypes.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.name}
                  </MenuItem>
                ))}
              </SelectWCreate>
            </Grid>
            <Grid item sm={5}>
              <Label>Legal Identifier Value *</Label>
              <ControlledTextInput
                name={`value`}
                type="text"
                fullWidth
                label=""
                variant="outlined"
                disabled={!clientLegal.isEditable}
                size="small"
                inputProps={{}}
              />
            </Grid>
          </Grid>

          <DrawerFooter>
            <Stack direction="row" justifyContent="flex-end" m={2} spacing={2}>
              <Button
                size="large"
                color="error"
                onClick={() => {
                  loadTabData && loadTabData(0);
                  showModal && showModal();
                }}
              >
                {`Cancel`}
              </Button>
              <WsButton
                type="submit"
                variant="contained"
                size="large"
                disabled={isLoading}
                endIcon={isLoading ? <CircularProgress size={17} color="secondary" /> : undefined}
              >
                {isLoading ? 'Saving...' : editFlag ? 'Update' : `Save`}
              </WsButton>
            </Stack>
          </DrawerFooter>
        </Box>
      </FormProvider>

      <DialogWithInput
        openDialog={openDialog}
        closeDialog={() => setOpenDialog(false)}
        onInput={(data) => onAddLegalIdentifier(data)}
        name="Legal Identifier"
        requireCountriesSelect={true}
      />
    </>
  );
};

export default ClientLegalIdentifierForm;
